// src/hooks/useGoogleLogin.ts
import { useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { TYPED_ENV } from "@/utils/env";
import { IdentityAnswer } from "@/pages/quiz/base/questions/calculatingYourPotentialWeightLoss";

export const useGoogleAuth = (onSuccess: (email: IdentityAnswer) => void) => {
  const tokenClientRef = useRef<google.accounts.oauth2.TokenClient | null>(
    null
  );

  const login = useCallback(() => {
    if (!window.google || !window.google.accounts?.oauth2) {
      toast.error("Google Identity Services not loaded.");
      return;
    }

    if (!tokenClientRef.current) {
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: TYPED_ENV.VITE_GOOGLE_CLIENT_ID,
        scope: "openid email profile",
        prompt: "", // Enables FedCM where supported
        callback: async (tokenResponse) => {
          if (!tokenResponse || !tokenResponse.access_token) {
            toast.error("Google login failed: no access token received.");
            return;
          }

          try {
            const res = await fetch(
              "https://www.googleapis.com/oauth2/v3/userinfo",
              {
                headers: {
                  Authorization: `Bearer ${tokenResponse.access_token}`,
                },
              }
            );

            if (!res.ok) {
              const error = await res.json();
              throw new Error(error?.error || "Failed to fetch user info");
            }

            const data = await res.json();
            const email = data?.email;

            if (email) {
              onSuccess({
                email,
                firstName: data.given_name,
                lastName: data.family_name,
              });
            } else {
              toast.error("Email not found in Google response.");
            }
          } catch (err) {
            console.error("Failed to fetch user info:", err);

            const message =
              err instanceof Error
                ? err.message
                : (err as any)?.error || "Unknown error occurred";

            toast.error(`Failed to fetch user info: ${message}`);
          }
        },
      });
    }

    try {
      tokenClientRef.current.requestAccessToken();
    } catch (err) {
      console.error("Google login request failed:", err);

      const message =
        err instanceof Error
          ? err.message
          : (err as any)?.error_description || "Google login failed.";

      toast.error(message);
    }
  }, [onSuccess]);

  return { login };
};
